import { useTranslation } from 'react-i18next';
import { FullScreen, useFullScreenHandle } from 'react-full-screen';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Grid,
  Link,
  IconButton,
  Tooltip,
  Zoom,
  CircularProgress,
} from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import FullscreenIcon from '@material-ui/icons/Fullscreen';
import GetAppIcon from '@material-ui/icons/GetApp';
import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import Report from '../../../components/Report';
import useStyles from '../styles';
import { getReport } from '../../../requests/api/report';

const DrawerTooltip = withStyles((theme) => ({
  tooltip: {
    // `secondary.blue` is exactly `custom.brandTooltipBackground`.
    backgroundColor: theme.palette.custom.brandTooltipBackground,
    color: theme.palette.custom.tooltipText,
    textAlign: 'center',
    width: '95px',
    maxWidth: '100%',
    fontSize: '1em',
  },
  arrow: {
    // Was `primary.main`, which never matched the tooltip's own background.
    color: theme.palette.custom.brandTooltipBackground,
    border: '0px',
  },
}))(Tooltip);

function ReportView() {
  const styles = useStyles();
  const { t } = useTranslation();
  const { id } = useParams();
  const [reportName, setReportName] = useState('');
  const [scheduleMessage, setScheduleMessage] = useState('');
  const [reportLoaded, setReportLoaded] = useState(false);
  const handle = useFullScreenHandle();
  const reportRef = useRef(null);
  const [openDownloadDialog, setOpenDownloadDialog] = useState(false);
  const [enabledDownloadPdf, setEnabledDownloadPdf] = useState(false);

  useEffect(() => {
    const parseScheduleMessage = (message = '') => message.replace(
      /\b(\d{2}):(\d{2})\b/g,
      (_, hour, minute) => {
        const d = new Date();
        const utc = new Date(
          Date.UTC(
            d.getUTCFullYear(),
            d.getUTCMonth(),
            d.getUTCDate(),
            +hour,
            +minute,
          ),
        );

        return `${utc.getHours().toString().padStart(2, '0')}:${utc
          .getMinutes()
          .toString()
          .padStart(2, '0')}`;
      },
    );

    const findReport = async () => {
      const response = await getReport(id);

      if (response.success) {
        const powerbiData = response.data.data.powerbiReport;

        setReportName(powerbiData.reportName);
        setScheduleMessage(
          parseScheduleMessage(powerbiData.scheduleMessage || ''),
        );
      }
    };

    findReport();
  }, [id, t]);

  const handleOpenDownloadDialog = () => {
    setOpenDownloadDialog(true);
  };

  const handleConfirmDownload = () => {
    setOpenDownloadDialog(false);
    if (reportRef.current) {
      reportRef.current.downloadPDF();
    }
  };

  return (
    <>
      <br />
      <div className={styles.content}>
        <div>
          <Grid container alignItems="center" justifyContent="space-between">
            <Grid item>
              <Typography
                className={styles.title}
                variant="h1"
                noWrap
                component={Link}
                gutterBottom
                display="inline"
              >
                <Link href="/reports" underline="none">
                  {t('reports.title')}
                </Link>
              </Typography>
              <Typography
                variant="subtitle1"
                className={styles.underlined}
                gutterBottom
                display="inline"
              >
                {' / '} {reportName}
              </Typography>
              {scheduleMessage && (
                <Typography variant="h4" gutterBottom>
                  {scheduleMessage}
                </Typography>
              )}
            </Grid>
            <Grid item>
              {enabledDownloadPdf && (
                <DrawerTooltip
                  TransitionComponent={Zoom}
                  placement="left"
                  title={t('reports.downloadPdf')}
                  arrow
                >
                  <span>
                    <IconButton
                      onClick={handleOpenDownloadDialog}
                      disabled={!reportLoaded}
                    >
                      {reportLoaded ? (
                        <GetAppIcon fontSize="large" />
                      ) : (
                        <CircularProgress size={24} />
                      )}
                    </IconButton>
                  </span>
                </DrawerTooltip>
              )}
              <DrawerTooltip
                TransitionComponent={Zoom}
                placement="right"
                title={t('reports.fullScreen')}
                arrow
              >
                <IconButton onClick={handle.enter}>
                  <FullscreenIcon fontSize="large" />
                </IconButton>
              </DrawerTooltip>
            </Grid>
          </Grid>
        </div>
        <br />
        <FullScreen handle={handle}>
          <Report
            ref={reportRef}
            fullScreen={handle.active}
            onReportLoaded={setReportLoaded}
            onEnabledDownloadPdfChange={setEnabledDownloadPdf}
          />
        </FullScreen>
      </div>
      <Dialog
        open={openDownloadDialog}
        onClose={() => setOpenDownloadDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6" align="center">
            {t('reports.reportExportPreparation')}
          </Typography>
        </DialogTitle>

        <DialogContent>
          <Box py={1}>
            <Typography variant="body1" gutterBottom>
              {t('reports.reportExportDescription')}
            </Typography>

            <Box mt={3}>
              <Typography variant="subtitle1" gutterBottom>
                <span aria-hidden="true">📄</span> {t('reports.pageOrientation')}
              </Typography>

              <Typography variant="body2" color="textSecondary">
                {t('reports.pageOrientationDescription')}
              </Typography>
            </Box>

            <Box mt={3}>
              <Typography variant="subtitle1" gutterBottom>
                <span aria-hidden="true">🎨</span> {t('reports.backgroundGraphicsAndColors')}
              </Typography>

              <Typography variant="body2" color="textSecondary">
                {t('reports.backgroundGraphicsRecommendation')}
              </Typography>

              <Typography
                variant="body2"
                color="textSecondary"
                style={{ marginTop: 8 }}
              >
                {t('reports.backgroundGraphicsWarning')}
              </Typography>
            </Box>
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpenDownloadDialog(false)}>
            {t('reports.cancel')}
          </Button>
          <Button
            color="primary"
            variant="contained"
            onClick={handleConfirmDownload}
          >
            {t('reports.continue')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default ReportView;
